import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ActivationPanel } from '../../components/ActivationPanel'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'
import { CityTypeahead } from '../../components/CityTypeahead'
import { RecordLink } from '../../components/RecordLink'
import { apiErrorMessage } from '../../lib/errors'
import { AgentsPanel } from '../agents/AgentsPanel'
import { DocumentsPanel } from '../documents/DocumentsPanel'
import { SharedContactsNotice } from '../sharedContacts/SharedContactsNotice'
import { FmcsaVerificationPanel } from '../fmcsa/FmcsaVerificationPanel'
import type { FmcsaVerification } from '../fmcsa/api'
import { JUST_SAVED, useArrivedFromSave } from '../fmcsa/arrival'
import { parseAmount } from '../../lib/number'
import { formatPhoneTyping } from '../../lib/phone'
import {
  getBroker,
  registerBroker,
  updateBroker,
  type BrokerDetail,
  type BrokerPayload,
} from './api'
import { BrokerField } from './BrokerField'
import { CHANGED_ELSEWHERE, messageFor, toFormErrors, withoutErrorsFor, type FormErrors } from './errors'
import { formatBondAmount, formatMcNumber } from './format'

const emptyForm = {
  name: '',
  mcNumber: '',
  usdotNumber: '',
  taxId: '',
  bondAmount: '',
  creditRating: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  phone: '',
  email: '',
}

type BrokerForm = typeof emptyForm

const noErrors: FormErrors = { fields: {}, summary: [] }

/** The record as the form edits it — used on load and again after every save. */
function toForm(broker: BrokerDetail): BrokerForm {
  return {
    name: broker.name,
    // Shown the way the book shows it: what you read in the list is what you edit.
    mcNumber: formatMcNumber(broker.mcNumber) ?? '',
    usdotNumber: broker.usdotNumber ?? '',
    taxId: broker.taxId ?? '',
    bondAmount: broker.bondAmount === 0 ? '' : String(broker.bondAmount),
    creditRating: broker.creditRating ?? '',
    line1: broker.address.line1,
    line2: broker.address.line2 ?? '',
    city: broker.address.city,
    state: broker.address.state,
    postalCode: broker.address.postalCode,
    phone: broker.phone ?? '',
    email: broker.email ?? '',
  }
}

/**
 * What the DAT paste sends a new broker off with (the capture, 2.2): the company's name
 * and the contact DAT printed, carried in the address's fragment — which never leaves the
 * browser. A rep's extension is theirs, not the brokerage's line, so the phone keeps its
 * main number only. Null when the address carries no draft.
 */
function draftFromPaste(hash: string): BrokerForm | null {
  const draft = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const name = draft.get('name')?.trim() ?? ''

  if (name === '') {
    return null
  }

  const phone = (draft.get('phone') ?? '').replace(/\s*(x|ext\.?|#)\s*\d+\s*$/i, '')

  return {
    ...emptyForm,
    name,
    email: draft.get('email')?.trim() ?? '',
    phone: formatPhoneTyping('', phone),
  }
}

/**
 * One form for both registering and editing: the API validates the same payload either
 * way, so a second near-identical screen would only be a second place to fix bugs.
 */
export function BrokerFormPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isEditing = id !== undefined

  // Read once, when the form opens to register — the one place that rule lives: the paste's
  // draft is a starting point, never something an edit takes, so it is null on an edit.
  const [fromPaste] = useState(() => (isEditing ? null : draftFromPaste(location.hash)))
  const [form, setForm] = useState<BrokerForm>(fromPaste ?? emptyForm)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  // Kept apart from the field errors: a broker that could not be read must never leave an
  // editable blank form on screen, or saving it would overwrite the record with nothing.
  // The failure is stored unresolved and translated at render, so this effect never needs
  // `t` — see the dependency note below.
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  // Kept beside the form rather than in it: the register's answer is not editable.
  const [fmcsaVerification, setFmcsaVerification] = useState<FmcsaVerification | null>(null)
  // Beside it for the same reason: whether we still work with them is stated by a
  // button of its own, never typed into a field and saved with the address.
  const [activation, setActivation] = useState<{ active: boolean; deactivatedAt: string | null }>({
    active: true,
    deactivatedAt: null,
  })
  // What was loaded, to tell an edited form from an untouched one — the register is
  // asked about the saved record, never about what is merely typed.
  const [savedForm, setSavedForm] = useState<BrokerForm>(emptyForm)
  // Bumped on every save so the verification panel remounts and re-asks the
  // register if the numbers changed; the panel's own memory is per mount on purpose.
  const [panelKey, setPanelKey] = useState(0)
  const [justSaved, setJustSaved] = useState(false)
  // The reading this form was opened on. Set when the record loads and when this form's
  // own save answers — never by the panels below, whose re-reads would silently adopt
  // somebody else's newer version and let the next save overwrite it.
  const [version, setVersion] = useState<string | null>(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  // Registering lands here from the blank form: the arrival is the save, and the one
  // time the register is asked without anybody pressing the button.
  const arrivedFromSave = useArrivedFromSave()
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (id === undefined) {
      return
    }

    let active = true

    // Re-entered when the id changes, not only on mount: leaving the previous broker
    // editable while the new one loads would let a save write the old values to the new
    // record.
    setIsLoading(true)
    setLoadFailure(null)
    setErrors(noErrors)

    void (async () => {
      try {
        const broker = await getBroker(id)
        if (!active) {
          return
        }

        const loaded = toForm(broker)

        setForm(loaded)
        setSavedForm(loaded)
        setVersion(broker.updatedAt)
        setFmcsaVerification(broker.fmcsaVerification)
        setActivation({ active: broker.active, deactivatedAt: broker.deactivatedAt })
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    // The visitor may leave before the fetch lands; do not set state on a dead form.
    return () => {
      active = false
    }
    // Only the id. react-i18next hands out a new `t` on every language change, and with
    // `t` in here switching languages mid-edit would refetch the broker and overwrite
    // everything typed but not yet saved.
  }, [id])

  function set(field: keyof BrokerForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    // "Saved." is a statement about the record; the first keystroke makes it untrue.
    setJustSaved(false)

    // The complaint about a field stops applying the moment it is edited — leaving it up
    // would have the form arguing with what the user is currently typing.
    setErrors((current) => withoutErrorsFor(current, field))
  }

  async function loadWhatIsSaved() {
    if (id === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const broker = await getBroker(id)
      const loaded = toForm(broker)

      setForm(loaded)
      setSavedForm(loaded)
      setVersion(broker.updatedAt)
      setFmcsaVerification(broker.fmcsaVerification)
      setActivation({ active: broker.active, deactivatedAt: broker.deactivatedAt })
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)

    // An empty bond is "not recorded", which the API stores as zero; anything typed has
    // to be a real number before it travels.
    const bondAmount = form.bondAmount.trim() === '' ? 0 : parseAmount(form.bondAmount)
    if (bondAmount === null) {
      setErrors({ fields: { bondAmount: 'invalid_bond' }, summary: [] })
      return
    }

    const payload: BrokerPayload = {
      name: form.name.trim(),
      mcNumber: blankToNull(form.mcNumber),
      usdotNumber: blankToNull(form.usdotNumber),
      taxId: blankToNull(form.taxId),
      bondAmount,
      creditRating: blankToNull(form.creditRating),
      address: {
        line1: form.line1.trim(),
        line2: blankToNull(form.line2),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
      },
      phone: blankToNull(form.phone),
      email: blankToNull(form.email),
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      if (id === undefined) {
        const created = await registerBroker(payload)

        // Straight to the record, not the book (TRKC-79): the register is asked the
        // moment a broker with numbers is saved, and its answer belongs beside the
        // broker it is about — the book has no room for a warning.
        void navigate(`/brokers/${created.id}`, { replace: true, state: JUST_SAVED })
      } else {
        const updated = await updateBroker(id, payload)

        // Stay. The form takes the record as the server normalized it, and the panel
        // below remounts so that, if the numbers changed, it asks the register again
        // right here — where the person who just typed the new MC number is looking.
        const refreshed = toForm(updated)
        setForm(refreshed)
        setSavedForm(refreshed)
        setVersion(updated.updatedAt)
        setFmcsaVerification(updated.fmcsaVerification)
        setPanelKey((key) => key + 1)
        setJustSaved(true)
      }
    } catch (caught) {
      // The server sends every broken rule at once, each with its code; they land on the
      // fields they belong to so the whole form can be fixed in one pass.
      setErrors(toFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <p className="session-check">{t('brokers.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs trail={[{ label: t('nav.brokers'), to: '/brokers' }, { label: t('brokers.editTitle') }]} />
        <header className="page__header">
          <h1>{t('brokers.editTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'brokers.loadError')}
        </p>
        <p>
          <Link to="/brokers">{t('brokers.backToList')}</Link>
        </p>
      </section>
    )
  }

  // The register answers about the SAVED broker, so verifying a form that has been
  // edited would print an answer about the previous numbers beside the new ones.
  // The panel is held shut until the edit lands.
  const hasUnsavedChanges = (Object.keys(form) as (keyof BrokerForm)[]).some(
    (key) => form[key] !== savedForm[key],
  )

  // Resolved here, on every render: the codes live in state, so switching language
  // re-translates every complaint already on screen instead of leaving it in the
  // language the save happened to fail in.
  const fieldText = (name: keyof BrokerForm) => {
    const code = errors.fields[name]
    return code === undefined
      ? undefined
      : messageFor(code, t, 'brokers.saveError', errors.subjects?.[code])
  }

  // A number another record already holds — in either book (TRKC-83): the sentence
  // names that record, and the way to it rides along, so "correct the original
  // first" is one click rather than a search.
  const field = (name: keyof BrokerForm) => {
    const code = errors.fields[name]
    const subject = code === undefined ? undefined : errors.subjects?.[code]
    const text = fieldText(name)

    return subject === undefined ? text : (
      <>
        {text} <RecordLink subject={subject} />
      </>
    )
  }

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.brokers'), to: '/brokers' },
          // The name as saved, never as it is being typed.
          { label: isEditing ? savedForm.name : t('breadcrumbs.newBroker') },
        ]}
      />
      <header className="page__header">
        <h1>{isEditing ? t('brokers.editTitle') : t('brokers.registerTitle')}</h1>
        <p className="page__subtitle">{t('brokers.formSubtitle')}</p>
        {fromPaste !== null && <p className="field__hint">{t('brokers.fromPaste')}</p>}
      </header>

      {/* Judged on the saved version: a save that changed the details is judged afresh,
          and what is being typed is never judged at all (TRKC-99). */}
      {id !== undefined && <SharedContactsNotice kind="Broker" id={id} version={version} />}

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice onLoadSaved={() => void loadWhatIsSaved()} isLoading={isLoadingSaved} />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{messageFor(code, t, 'brokers.saveError', errors.subjects?.[code])}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset">
          <legend>{t('brokers.identity')}</legend>

          <BrokerField
            name="name"
            label={t('brokers.name')}
            value={form.name}
            onChange={(value) => set('name', value)}
            error={field('name')}
            maxLength={200}
            required
            width="wide"
          />
          <BrokerField
            name="mcNumber"
            label={t('brokers.mcNumber')}
            value={form.mcNumber}
            onChange={(value) => set('mcNumber', value)}
            error={field('mcNumber')}
            placeholder="MC-123456"
          />
          <BrokerField
            name="usdotNumber"
            label={t('brokers.usdotNumber')}
            value={form.usdotNumber}
            onChange={(value) => set('usdotNumber', value)}
            error={field('usdotNumber')}
          />
          <BrokerField
            name="taxId"
            label={t('brokers.taxId')}
            value={form.taxId}
            onChange={(value) => set('taxId', value)}
            error={field('taxId')}
            maxLength={32}
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend>{t('brokers.address')}</legend>

          <BrokerField
            name="line1"
            label={t('brokers.line1')}
            value={form.line1}
            onChange={(value) => set('line1', value)}
            error={field('line1')}
            maxLength={200}
            required
            width="wide"
          />
          <BrokerField
            name="line2"
            label={t('brokers.line2')}
            value={form.line2}
            onChange={(value) => set('line2', value)}
            error={field('line2')}
            maxLength={200}
            width="wide"
          />
          <CityTypeahead
            name="city"
            label={t('brokers.city')}
            value={form.city}
            onChange={(value) => set('city', value)}
            // Picking "Springfield, IL" fills both boxes — and both set() calls clear
            // their field's standing complaints, the state's included. Typing free
            // text never touches the state.
            onSelect={({ city, state }) => {
              set('city', city)
              set('state', state)
            }}
            error={fieldText('city')}
            maxLength={100}
            required
            preferredState={form.state}
          />
          <BrokerField
            name="state"
            label={t('brokers.state')}
            value={form.state}
            onChange={(value) => set('state', value)}
            error={field('state')}
            maxLength={2}
            placeholder="FL"
            required
            width="narrow"
          />
          <BrokerField
            name="postalCode"
            label={t('brokers.postalCode')}
            value={form.postalCode}
            onChange={(value) => set('postalCode', value)}
            error={field('postalCode')}
            maxLength={10}
            placeholder="33131"
            required
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend>{t('brokers.commercial')}</legend>

          <BrokerField
            name="bondAmount"
            label={t('brokers.bondAmount')}
            value={form.bondAmount}
            onChange={(value) => set('bondAmount', value)}
            error={field('bondAmount')}
            inputMode="decimal"
            // Reads as the money it is while at rest; hands back the raw number to edit.
            display={(value) => formatBondAmount(value, i18n.language)}
          />
          <BrokerField
            name="creditRating"
            label={t('brokers.creditRating')}
            value={form.creditRating}
            // Uppercased as typed — the screen shows the exact spelling the domain will
            // store, instead of quietly correcting it after the save.
            onChange={(value) => set('creditRating', value.toUpperCase())}
            error={field('creditRating')}
            maxLength={16}
            placeholder="A+"
            width="narrow"
          />
          <BrokerField
            name="phone"
            label={t('brokers.phone')}
            value={form.phone}
            // Dressed as it is typed — same box manners on every phone in the app.
            onChange={(value) => set('phone', formatPhoneTyping(form.phone, value))}
            error={field('phone')}
            type="tel"
            placeholder="(305) 555-0142"
          />
          <BrokerField
            name="email"
            label={t('brokers.email')}
            value={form.email}
            onChange={(value) => set('email', value)}
            error={field('email')}
            type="email"
            maxLength={256}
          />
        </fieldset>

        <div className="form__actions">
          {justSaved && (
            <p className="page__subtitle" role="status">
              {t('brokers.saved')}
            </p>
          )}
          <button type="submit" className="button" disabled={isSubmitting}>
            {isSubmitting ? t('brokers.saving') : t('brokers.save')}
          </button>
        </div>
      </form>

      {/* Registration first, everything else second: both panels need a broker to exist. */}
      {id !== undefined && (
        <FmcsaVerificationPanel
          key={panelKey}
          ownerType="Broker"
          ownerId={id}
          storedVerification={fmcsaVerification}
          hasUnsavedChanges={hasUnsavedChanges}
          hasIdentifiers={savedForm.mcNumber.trim() !== '' || savedForm.usdotNumber.trim() !== ''}
          // A save, either kind: the registration that landed here, or the edit that
          // just remounted the panel. Opening the record from the book is neither.
          askOnMount={justSaved || arrivedFromSave}
        />
      )}
      {/* Keyed by the broker: a save still in flight for the previous one must not
          land in this one's list. */}
      {id !== undefined && <AgentsPanel key={id} brokerId={id} />}
      {id !== undefined && <DocumentsPanel ownerType="Broker" ownerId={id} />}
      {id !== undefined && (
        <ActivationPanel
          ownerType="Broker"
          ownerId={id}
          active={activation.active}
          deactivatedAt={activation.deactivatedAt}
          onChanged={(active, deactivatedAt) => setActivation({ active, deactivatedAt })}
        />
      )}

      {/* Saving no longer leaves this page, so the page has to offer the way out. */}
      <p>
        <Link to="/brokers">{t('brokers.backToList')}</Link>
      </p>
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
